import { useEffect, useState, useCallback } from 'react';
import {
  Loader2, Plus, Trash2, Bell, Mail, FlaskConical, MapPin, CheckCircle2, AlertCircle, Save,
} from 'lucide-react';
import pestModelService from '../../services/pestModels';
import { userService, type UserData } from '../../services/user';
import { errorMessage } from '../../utils/apiResponse';
import type {
  PestModel, MatchedFarm, PestAlertRule, PestNotifyAction, PestRecipientSpec,
  PestLeafCondition, PestConditionMetric, ConditionOp, RiskLevel, DegreeDayParams,
  PestCondition, PestAlertTrigger, ProtectionEventType,
} from '../../types/pest';
import { RISK_LEVELS, INFECTION_MODEL_TYPES, PROTECTION_EVENT_TYPES } from '../../types/pest';
import { riskLabel } from '../../utils/pestRisk';

// Compute the current season year for a model client-side (mirrors backend).
function currentSeasonYear(params: unknown): number {
  const now = new Date();
  const p = params as { season?: { start?: { month: number; day: number } } } | undefined;
  const start = p?.season?.start || { month: 8, day: 1 };
  const y = now.getFullYear();
  const startThisYear = new Date(y, start.month - 1, start.day);
  return now >= startThisYear ? y : y - 1;
}

const METRIC_LABELS: Record<PestConditionMetric, string> = {
  risk_level: 'Risk level',
  value_cum: 'Accumulated value (DD / infections)',
  value_day: 'Daily value',
  risk_score: 'Risk score (0–100)',
  rain_mm: 'Rain that day (mm)',
  rain_3d_mm: 'Rain over 3 days (mm)',
};

const PROTECTION_EVENT_LABELS: Record<ProtectionEventType, { label: string; hint: string }> = {
  curative_window: { label: 'Curative window open', hint: 'An infection started with no protectant on. Says how long a curative still works.' },
  forecast_unprotected: { label: 'Infection forecast on unprotected blocks', hint: 'Time to put a protectant on before the rain.' },
  washed_off: { label: 'Protectant washed off', hint: 'Rain since the last protectant passed its wash-off figure (25 mm unless the chemical says otherwise).' },
  washoff_forecast: { label: 'Wash-off forecast', hint: 'Forecast rain will take the protectant past its wash-off figure.' },
};

/** A stored condition as an editable list of leaves plus how they combine. */
function conditionRows(c: PestCondition | null | undefined): { combinator: 'all' | 'any'; leaves: PestLeafCondition[] } {
  const fallback: PestLeafCondition[] = [{ metric: 'risk_level', op: 'gte', value: 'high' }];
  if (!c) return { combinator: 'all', leaves: fallback };
  if ('metric' in c) return { combinator: 'all', leaves: [c as PestLeafCondition] };
  if (c.any?.length) return { combinator: 'any', leaves: c.any };
  return { combinator: 'all', leaves: c.all?.length ? c.all : fallback };
}

function describeRule(rule: PestAlertRule): string {
  if (rule.trigger_type === 'protection') {
    const ev = rule.protection_events?.length ? rule.protection_events : PROTECTION_EVENT_TYPES;
    return ev.map((e) => PROTECTION_EVENT_LABELS[e].label.toLowerCase()).join(', ');
  }
  const { combinator, leaves } = conditionRows(rule.condition);
  return leaves
    .map((l) => `${METRIC_LABELS[l.metric] ?? l.metric} ${OP_LABELS[l.op]} ${l.metric === 'risk_level' ? riskLabel(String(l.value)) : l.value}`)
    .join(combinator === 'all' ? ' and ' : ' or ');
}
const OP_LABELS: Record<ConditionOp, string> = {
  gte: '≥', gt: '>', lte: '≤', lt: '<', eq: '=', neq: '≠',
};

// ─── Matched farms + biofix ──────────────────────────────────────────────────

export function PestFarmsPanel({ pest, canManage }: { pest: PestModel; canManage: boolean }) {
  const [farms, setFarms] = useState<MatchedFarm[] | null>(null);
  const [busy, setBusy] = useState<number | null>(null);
  const isDegreeDay = pest.model_type === 'degree_day';
  const season = currentSeasonYear(pest.model_params);

  const load = useCallback(async () => {
    try { setFarms(await pestModelService.getMatchedFarms(pest.id)); }
    catch { setFarms([]); }
  }, [pest.id]);
  useEffect(() => { load(); }, [load]);

  const toggle = async (farm: MatchedFarm, enabled: boolean) => {
    setBusy(farm.id);
    try {
      await pestModelService.setActivation(pest.id, { farm_id: farm.id, enabled });
      await load();
    } catch (e) { alert(errorMessage(e)); }
    finally { setBusy(null); }
  };

  if (!farms) return <div className="flex items-center gap-2 text-sm text-neutral-500 py-4"><Loader2 className="h-4 w-4 animate-spin" /> Loading farms…</div>;
  if (!farms.length) return <p className="text-sm text-neutral-500 py-4">No farms grow a host of this pest yet. Add host crop types, or plant a matching cultivar area.</p>;

  return (
    <div className="space-y-2">
      <p className="text-xs text-neutral-500">Farms growing a host crop are monitored automatically. Toggle to opt a farm out or in.</p>
      {farms.map((f) => {
        const monitored = f.activation_enabled !== false;
        return (
          <div key={f.id} className="flex flex-wrap items-center gap-3 border border-neutral-200 rounded-lg px-3 py-2">
            <MapPin className="h-4 w-4 text-neutral-400" />
            <span className="font-medium text-sm text-neutral-800">{f.name}</span>
            <span className="text-[11px] px-1.5 py-0.5 rounded bg-neutral-100 text-neutral-500">{f.auto ? 'auto-matched' : 'manual'}</span>
            <label className="ml-auto inline-flex items-center gap-1.5 text-sm">
              <input type="checkbox" checked={monitored} disabled={!canManage || busy === f.id} onChange={(e) => toggle(f, e.target.checked)} />
              Monitored
            </label>
            {isDegreeDay && monitored && (
              <BiofixControl pestId={pest.id} farmId={f.id} season={season} canManage={canManage} params={pest.model_params as DegreeDayParams} />
            )}
          </div>
        );
      })}
    </div>
  );
}

function BiofixControl({ pestId, farmId, season, canManage, params }: {
  pestId: number; farmId: number; season: number; canManage: boolean; params: DegreeDayParams;
}) {
  const [date, setDate] = useState('');
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    let live = true;
    pestModelService.getBiofix(pestId, farmId, season).then((b) => { if (live && b) setDate(b.biofix_date); });
    return () => { live = false; };
  }, [pestId, farmId, season]);

  const fallback = params?.biofix_fallback;
  const save = async () => {
    if (!date) return;
    setBusy(true);
    try {
      await pestModelService.setBiofix(pestId, { farm_id: farmId, season_year: season, biofix_date: date, source: 'manual' });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) { alert(errorMessage(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="w-full flex items-center gap-2 text-xs mt-1 pl-6">
      <span className="text-neutral-500">Biofix {season}:</span>
      <input type="date" value={date} disabled={!canManage} onChange={(e) => setDate(e.target.value)}
        className="border border-neutral-200 rounded px-2 py-1 text-xs" />
      {canManage && (
        <button onClick={save} disabled={busy || !date} className="px-2 py-1 rounded bg-neutral-100 hover:bg-neutral-200 disabled:opacity-50 inline-flex items-center gap-1">
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : saved ? <CheckCircle2 className="h-3 w-3 text-emerald-600" /> : <Save className="h-3 w-3" />} Set
        </button>
      )}
      {!date && fallback && <span className="text-neutral-400">defaults to {fallback.day}/{fallback.month}</span>}
    </div>
  );
}

// ─── Alert rules ─────────────────────────────────────────────────────────────

export function PestAlertRulesPanel({ pest, canManage }: { pest: PestModel; canManage: boolean }) {
  const [rules, setRules] = useState<PestAlertRule[] | null>(null);
  const [farms, setFarms] = useState<MatchedFarm[]>([]);
  const [users, setUsers] = useState<UserData[]>([]);
  const [editing, setEditing] = useState<PestAlertRule | PestAlertTrigger | null>(null);
  const isInfection = INFECTION_MODEL_TYPES.includes(pest.model_type);

  const load = useCallback(async () => {
    try { setRules(await pestModelService.listRules(pest.id)); } catch { setRules([]); }
  }, [pest.id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    pestModelService.getMatchedFarms(pest.id).then(setFarms).catch(() => setFarms([]));
    userService.getUsers().then((r) => { if (r.success && r.data) setUsers(r.data); }).catch(() => {});
  }, [pest.id]);

  const test = async (rule: PestAlertRule) => {
    try { const r = await pestModelService.testRule(rule.id); alert(`Would alert on ${r.fired} of ${r.checked} farm(s) right now.`); }
    catch (e) { alert(errorMessage(e)); }
  };
  const remove = async (rule: PestAlertRule) => {
    if (!confirm(`Delete alert rule "${rule.name}"?`)) return;
    try { await pestModelService.deleteRule(rule.id); await load(); } catch (e) { alert(errorMessage(e)); }
  };

  if (!rules) return <div className="flex items-center gap-2 text-sm text-neutral-500 py-4"><Loader2 className="h-4 w-4 animate-spin" /> Loading rules…</div>;

  if (editing) {
    return (
      <PestRuleForm
        pest={pest} farms={farms} users={users}
        rule={typeof editing === 'string' ? null : editing}
        initialTrigger={typeof editing === 'string' ? editing : editing.trigger_type}
        onDone={async () => { setEditing(null); await load(); }}
        onCancel={() => setEditing(null)}
      />
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs text-neutral-500">Alerts notify recipients before a forecast risk window. Uses the modelled + forecast risk series.</p>
        {canManage && (
          <button onClick={() => setEditing('risk')} className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg bg-primary-600 text-white hover:bg-primary-700 shrink-0">
            <Plus className="h-4 w-4" /> New alert rule
          </button>
        )}
      </div>
      {isInfection && canManage && !rules.some((r) => r.trigger_type === 'protection') && (
        <div className="rounded-lg border border-sky-200 bg-sky-50 px-3 py-2.5 text-sm text-sky-900 flex flex-wrap items-center gap-2">
          <span className="flex-1 min-w-0">
            Get told when rain washes a protectant off, and how long a curative still works after an infection gets through.
          </span>
          <button onClick={() => setEditing('protection')} className="text-sm px-3 py-1.5 rounded-lg bg-white border border-sky-300 hover:bg-sky-100 shrink-0">
            Add wash-off &amp; curative alerts
          </button>
        </div>
      )}
      {!rules.length && <p className="text-sm text-neutral-400 py-4">No alert rules yet.</p>}
      {rules.map((rule) => (
        <div key={rule.id} className="border border-neutral-200 rounded-lg p-3">
          <div className="flex items-center gap-2">
            <span className={`h-2 w-2 rounded-full ${rule.enabled ? 'bg-emerald-500' : 'bg-neutral-300'}`} />
            <span className="font-medium text-sm break-words min-w-0">{rule.name}</span>
            <span className="text-xs text-neutral-500">
              {rule.farm_id ? (farms.find((f) => f.id === rule.farm_id)?.name ?? `farm #${rule.farm_id}`) : 'all matched farms'}
              {rule.lead_time_days > 0 ? ` · ${rule.lead_time_days}d lead` : ''}
            </span>
            <div className="ml-auto flex items-center gap-1">
              <button onClick={() => test(rule)} title="Test now" className="p-1.5 rounded hover:bg-neutral-100 text-neutral-500"><FlaskConical className="h-4 w-4" /></button>
              {canManage && <button onClick={() => setEditing(rule)} className="text-xs px-2 py-1 rounded hover:bg-neutral-100 text-neutral-600">Edit</button>}
              {canManage && <button onClick={() => remove(rule)} className="p-1.5 rounded hover:bg-red-50 text-neutral-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>}
            </div>
          </div>
          <div className="mt-1 text-xs text-neutral-500 break-words">
            {rule.trigger_type === 'protection' ? 'Protection: ' : 'When '}{describeRule(rule)}
          </div>
          {rule.last_error && <div className="mt-1.5 text-xs text-red-600 flex items-center gap-1"><AlertCircle className="h-3.5 w-3.5" /> {rule.last_error}</div>}
          {rule.last_fired_at && <div className="mt-1 text-[11px] text-neutral-400">last fired {new Date(rule.last_fired_at).toLocaleString()}</div>}
        </div>
      ))}
    </div>
  );
}

// ─── Rule form ───────────────────────────────────────────────────────────────

function PestRuleForm({ pest, farms, users, rule, initialTrigger, onDone, onCancel }: {
  pest: PestModel; farms: MatchedFarm[]; users: UserData[]; rule: PestAlertRule | null;
  initialTrigger: PestAlertTrigger; onDone: () => void; onCancel: () => void;
}) {
  const trigger: PestAlertTrigger = rule?.trigger_type ?? initialTrigger;
  const isProtection = trigger === 'protection';
  const initial = conditionRows(rule?.condition);
  const [name, setName] = useState(rule?.name ?? (isProtection ? `${pest.name} — wash-off & curative` : `${pest.name} — high risk`));
  const [farmId, setFarmId] = useState<number | null>(rule?.farm_id ?? null);
  const [enabled, setEnabled] = useState(rule?.enabled ?? true);
  const [combinator, setCombinator] = useState<'all' | 'any'>(initial.combinator);
  const [leaves, setLeaves] = useState<Array<{ metric: PestConditionMetric; op: ConditionOp; value: string }>>(
    initial.leaves.map((l) => ({ metric: l.metric, op: l.op, value: String(l.value) })),
  );
  const [events, setEvents] = useState<ProtectionEventType[]>(rule?.protection_events?.length ? rule.protection_events : PROTECTION_EVENT_TYPES);
  const [leadDays, setLeadDays] = useState<number>(rule?.lead_time_days ?? 3);
  const [cooldownDays, setCooldownDays] = useState<number>(rule ? Math.max(1, Math.round(rule.cooldown_sec / 86400)) : 1);
  const [actions, setActions] = useState<PestNotifyAction[]>(
    rule?.actions?.length ? rule.actions : [{ type: 'notify_in_app', user_ids: 'spray_admins' }]
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const setLeaf = (idx: number, patch: Partial<{ metric: PestConditionMetric; op: ConditionOp; value: string }>) =>
    setLeaves(leaves.map((l, i) => (i === idx ? { ...l, ...patch } : l)));

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const parsed: PestLeafCondition[] = leaves.map((l) => ({ metric: l.metric, op: l.op, value: l.metric === 'risk_level' ? l.value : Number(l.value) }));
      const condition: PestCondition | null = isProtection ? null : parsed.length === 1 ? parsed[0] : { [combinator]: parsed };
      const payload = {
        pest_model_id: pest.id,
        farm_id: farmId,
        name,
        enabled,
        condition,
        ...(isProtection ? { protection_events: events } : {}),
        lead_time_days: leadDays,
        cooldown_sec: Math.max(0, cooldownDays) * 86400,
        actions,
      };
      if (rule) await pestModelService.updateRule(rule.id, payload);
      else await pestModelService.createRule({ ...payload, trigger_type: trigger });
      onDone();
    } catch (e) { setError(errorMessage(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="border border-neutral-200 rounded-lg p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="font-semibold text-sm">{rule ? 'Edit alert rule' : isProtection ? 'New wash-off & curative alert' : 'New alert rule'}</h4>
        <label className="inline-flex items-center gap-1.5 text-sm"><input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} /> Enabled</label>
      </div>

      <label className="block">
        <span className="text-xs text-neutral-600">Name</span>
        <input value={name} onChange={(e) => setName(e.target.value)} className="mt-1 w-full border border-neutral-300 rounded px-2 py-1.5 text-sm" />
      </label>

      <label className="block">
        <span className="text-xs text-neutral-600">Applies to</span>
        <select value={farmId ?? ''} onChange={(e) => setFarmId(e.target.value ? Number(e.target.value) : null)} className="mt-1 w-full border border-neutral-300 rounded px-2 py-1.5 text-sm">
          <option value="">All matched farms</option>
          {farms.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
        </select>
      </label>

      {isProtection ? (
        <div className="space-y-1.5">
          <span className="text-xs text-neutral-600">Tell me when</span>
          {PROTECTION_EVENT_TYPES.map((ev) => (
            <label key={ev} className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                className="mt-1"
                checked={events.includes(ev)}
                onChange={(e) => setEvents(e.target.checked ? [...events, ev] : events.filter((x) => x !== ev))}
              />
              <span className="min-w-0">
                <span className="font-medium text-neutral-800">{PROTECTION_EVENT_LABELS[ev].label}</span>
                <span className="block text-xs text-neutral-500">{PROTECTION_EVENT_LABELS[ev].hint}</span>
              </span>
            </label>
          ))}
          <p className="text-xs text-neutral-500">Checked every three hours. Each event is sent once, naming the blocks it affects.</p>
        </div>
      ) : (
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-neutral-600">Fire when</span>
            {leaves.length > 1 && (
              <select value={combinator} onChange={(e) => setCombinator(e.target.value as 'all' | 'any')} className="border border-neutral-300 rounded px-2 py-1 text-xs">
                <option value="all">all of these are true</option>
                <option value="any">any of these is true</option>
              </select>
            )}
          </div>
          {leaves.map((leaf, idx) => (
            <div key={idx} className="flex flex-wrap items-center gap-2">
              <select
                value={leaf.metric}
                onChange={(e) => {
                  const m = e.target.value as PestConditionMetric;
                  setLeaf(idx, { metric: m, value: m === 'risk_level' ? 'high' : m.startsWith('rain') ? '25' : '100' });
                }}
                className="border border-neutral-300 rounded px-2 py-1.5 text-sm min-w-0 max-w-full"
              >
                {(Object.keys(METRIC_LABELS) as PestConditionMetric[]).map((m) => <option key={m} value={m}>{METRIC_LABELS[m]}</option>)}
              </select>
              <select value={leaf.op} onChange={(e) => setLeaf(idx, { op: e.target.value as ConditionOp })} className="border border-neutral-300 rounded px-2 py-1.5 text-sm">
                {(Object.keys(OP_LABELS) as ConditionOp[]).map((o) => <option key={o} value={o}>{OP_LABELS[o]}</option>)}
              </select>
              {leaf.metric === 'risk_level' ? (
                <select value={leaf.value} onChange={(e) => setLeaf(idx, { value: e.target.value })} className="border border-neutral-300 rounded px-2 py-1.5 text-sm">
                  {RISK_LEVELS.filter((l) => l !== 'none').map((l: RiskLevel) => <option key={l} value={l}>{riskLabel(l)}</option>)}
                </select>
              ) : (
                <input type="number" value={leaf.value} onChange={(e) => setLeaf(idx, { value: e.target.value })} className="border border-neutral-300 rounded px-2 py-1.5 text-sm w-24" />
              )}
              {leaves.length > 1 && (
                <button type="button" onClick={() => setLeaves(leaves.filter((_, i) => i !== idx))} className="p-1 text-neutral-400 hover:text-red-600" aria-label="Remove condition">
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>
          ))}
          <button type="button" onClick={() => setLeaves([...leaves, { metric: 'rain_3d_mm', op: 'gte', value: '25' }])} className="text-xs inline-flex items-center gap-1 px-2 py-1 rounded bg-neutral-100 hover:bg-neutral-200">
            <Plus className="h-3.5 w-3.5" /> Add condition
          </button>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <label className="block">
          <span className="text-xs text-neutral-600">{isProtection ? 'Look ahead for forecast events (days)' : 'Warn this many days ahead'}</span>
          <input type="number" min={0} max={16} value={leadDays} onChange={(e) => setLeadDays(Number(e.target.value))} className="mt-1 w-full border border-neutral-300 rounded px-2 py-1.5 text-sm" />
        </label>
        <label className="block">
          <span className="text-xs text-neutral-600">Min days between alerts</span>
          <input type="number" min={0} value={cooldownDays} onChange={(e) => setCooldownDays(Number(e.target.value))} className="mt-1 w-full border border-neutral-300 rounded px-2 py-1.5 text-sm" />
        </label>
      </div>

      {/* Actions */}
      <div className="space-y-2">
        <span className="text-xs text-neutral-600">Notify</span>
        {actions.map((a, idx) => (
          <RecipientEditor
            key={idx} action={a} users={users}
            onChange={(next) => setActions(actions.map((x, i) => (i === idx ? next : x)))}
            onRemove={() => setActions(actions.filter((_, i) => i !== idx))}
          />
        ))}
        <div className="flex gap-2">
          <button type="button" onClick={() => setActions([...actions, { type: 'notify_in_app', user_ids: 'spray_admins' }])} className="text-xs inline-flex items-center gap-1 px-2 py-1 rounded bg-neutral-100 hover:bg-neutral-200"><Bell className="h-3.5 w-3.5" /> In-app</button>
          <button type="button" onClick={() => setActions([...actions, { type: 'notify_email', user_ids: 'spray_admins' }])} className="text-xs inline-flex items-center gap-1 px-2 py-1 rounded bg-neutral-100 hover:bg-neutral-200"><Mail className="h-3.5 w-3.5" /> Email</button>
        </div>
      </div>

      {error && <p className="text-xs text-red-600">{error}</p>}
      <div className="flex gap-2 pt-1">
        <button onClick={save} disabled={saving || !name} className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save rule
        </button>
        <button onClick={onCancel} className="text-sm px-3 py-1.5 rounded-lg bg-neutral-100 hover:bg-neutral-200">Cancel</button>
      </div>
    </div>
  );
}

function RecipientEditor({ action, users, onChange, onRemove }: {
  action: PestNotifyAction; users: UserData[]; onChange: (a: PestNotifyAction) => void; onRemove: () => void;
}) {
  const spec = action.user_ids;
  const mode: 'spray_admins' | 'all_admins' | 'specific' = spec === 'spray_admins' ? 'spray_admins' : spec === 'all_admins' ? 'all_admins' : 'specific';
  const selectedIds = Array.isArray(spec) ? spec : [];
  const setSpec = (next: PestRecipientSpec) => onChange({ ...action, user_ids: next });
  const toggleUser = (id: number) => setSpec(selectedIds.includes(id) ? selectedIds.filter((u) => u !== id) : [...selectedIds, id]);

  return (
    <div className="border border-neutral-200 rounded-lg overflow-hidden">
      <div className="flex items-center gap-2 px-3 py-1.5 bg-neutral-50 border-b border-neutral-200">
        {action.type === 'notify_email' ? <Mail className="h-4 w-4 text-indigo-600" /> : <Bell className="h-4 w-4 text-blue-600" />}
        <span className="text-sm font-medium">{action.type === 'notify_email' ? 'Email' : 'In-app'}</span>
        <button type="button" onClick={onRemove} className="ml-auto text-neutral-400 hover:text-red-600 p-1"><Trash2 className="h-4 w-4" /></button>
      </div>
      <div className="p-2.5 space-y-2">
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <label className="inline-flex items-center gap-1.5"><input type="radio" checked={mode === 'spray_admins'} onChange={() => setSpec('spray_admins')} /> Spray admins</label>
          <label className="inline-flex items-center gap-1.5"><input type="radio" checked={mode === 'all_admins'} onChange={() => setSpec('all_admins')} /> All admins</label>
          <label className="inline-flex items-center gap-1.5"><input type="radio" checked={mode === 'specific'} onChange={() => setSpec([])} /> Specific users</label>
        </div>
        {mode === 'specific' && (
          <div className="max-h-28 overflow-y-auto border border-neutral-200 rounded p-2 space-y-1">
            {users.length === 0 && <div className="text-xs text-neutral-400 italic">No users found.</div>}
            {users.map((u) => (
              <label key={u.id} className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={selectedIds.includes(u.id)} onChange={() => toggleUser(u.id)} />
                <span>{u.first_name || u.last_name ? `${u.first_name || ''} ${u.last_name || ''}`.trim() : u.username}</span>
                <span className="text-xs text-neutral-400">{u.email}</span>
              </label>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
